/**
 * Projection configuration and helper functions for HEC-RAS Checker
 */

// Available projections with their display names and file paths
export const PROJECTIONS = {
    'NC': {
        label: 'NC',
        name: 'NC State Plane',
        file: '/projections/NC_StatePlane.prj'
    },
    'SC': {
        label: 'SC',
        name: 'SC State Plane',
        file: '/projections/SC_StatePlane.prj'
    },
    'AZ_CENTRAL': {
        label: 'AZ (Central)',
        name: 'AZ State Plane Central',
        file: '/projections/AZ_StatePlane_Central.prj'
    },
    'AZ_EAST': {
        label: 'AZ (East)',
        name: 'AZ State Plane East',
        file: '/projections/AZ_StatePlane_East.prj'
    },
    'AZ_WEST': {
        label: 'AZ (West)',
        name: 'AZ State Plane West',
        file: '/projections/AZ_StatePlane_West.prj'
    },
    'CA_ZONE5': {
        label: 'CA (Zone 5)',
        name: 'CA State Plane Zone V',
        file: '/projections/CA_StatePlane_V.prj'
    },
    'HI_ZONE3': {
        label: 'HI (Zone 3)',
        name: 'HI State Plane Zone 3',
        file: '/projections/HI_StatePlane_3.prj'
    }
};

// Default projection key
export const DEFAULT_PROJECTION = 'NC';

// Get projection options for select dropdown
export const getProjectionOptions = () => {
    return Object.entries(PROJECTIONS).map(([key, value]) => ({
        value: key,
        label: value.label
    }));
};

// Get projection by key
export const getProjection = (key) => {
    return PROJECTIONS[key] || PROJECTIONS[DEFAULT_PROJECTION];
};

// Get projection file path by key
export const getProjectionFilePath = (key) => {
    const proj = getProjection(key);
    return proj?.file;
};

// Get projection display name by key
export const getProjectionName = (key) => {
    const proj = getProjection(key);
    return proj?.name || key;
};

// Get projection label by key
export const getProjectionLabel = (key) => {
    const proj = getProjection(key);
    return proj?.label || key;
};

// Load projection text from file
export const loadProjectionText = async (key) => {
    const filePath = getProjectionFilePath(key);
    if (!filePath) return null;
    
    try {
        const response = await fetch(filePath);
        if (response.ok) {
            return await response.text();
        }
    } catch (error) {
        console.error(`Failed to load projection file: ${filePath}`, error);
    }
    return null;
};

// Find projection key by WKT text (for identifying project projection)
export const findProjectionKeyByText = (wktText) => {
    if (!wktText) return null;
    
    const normalizedInput = wktText.toLowerCase().replace(/\s+/g, '');
    
    for (const [key] of Object.entries(PROJECTIONS)) {
        // Simple heuristic: check if key identifiers exist in the WKT
        const keyLower = key.toLowerCase();
        if (keyLower === 'nc' && normalizedInput.includes('north_carolina')) {
            return key;
        }
        if (keyLower === 'sc' && normalizedInput.includes('south_carolina')) {
            return key;
        }
        if (keyLower === 'az_central' && normalizedInput.includes('arizona') && normalizedInput.includes('central')) {
            return key;
        }
        if (keyLower === 'az_east' && normalizedInput.includes('arizona') && normalizedInput.includes('east')) {
            return key;
        }
        if (keyLower === 'az_west' && normalizedInput.includes('arizona') && normalizedInput.includes('west')) {
            return key;
        }
        if (keyLower === 'ca_zone5' && normalizedInput.includes('california') && (normalizedInput.includes('zone_v') || normalizedInput.includes('zone_5'))) {
            return key;
        }
        if (keyLower === 'hi_zone3' && normalizedInput.includes('hawaii') && normalizedInput.includes('zone_3')) {
            return key;
        }
    }
    
    return null;
};

const ProjectionHelper = {
    PROJECTIONS,
    DEFAULT_PROJECTION,
    getProjectionOptions,
    getProjection,
    getProjectionFilePath,
    getProjectionName,
    getProjectionLabel,
    loadProjectionText,
    findProjectionKeyByText
};

export default ProjectionHelper;
